/**
 * Toggle Switch Component
 * A modern toggle switch for settings
 */

import type { FC, ReactNode } from 'react';

import { TOGGLE_SWITCH_VARIANT, type ToggleSwitchVariant } from './toggleSwitchConstants';
import styles from './ToggleSwitch.module.css';

interface ToggleSwitchProps {
  variant?: ToggleSwitchVariant;
  checked: boolean;
  onChange: (checked: boolean) => void;
  leftLabel?: ReactNode;
  rightLabel?: ReactNode;
  dataTestId?: string;
}

const ToggleSwitch: FC<ToggleSwitchProps> = ({
  variant = TOGGLE_SWITCH_VARIANT.DEFAULT,
  checked,
  onChange,
  leftLabel,
  rightLabel,
  dataTestId
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.checked);
  };

  const isSegmented = variant === TOGGLE_SWITCH_VARIANT.SEGMENTED;
  const hasLabels = leftLabel && rightLabel;

  return (
    <label
      className={`${styles.switchContainer} ${isSegmented ? styles.segmented : ''} ${checked ? styles.checked : ''}`}
      data-testid={dataTestId}
    >
      <input
        className={styles.switchInput}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
      />
      <span className={styles.slider} />
      {isSegmented && hasLabels && (
        <span className={styles.segmentedLabels}>
          <span className={styles.leftLabel}>{leftLabel}</span>
          <span className={styles.rightLabel}>{rightLabel}</span>
        </span>
      )}
    </label>
  );
};

export default ToggleSwitch;
